/**
 * How long the authentication client may stay unloaded before the dashboard stops waiting on it.
 *
 * Longer than the active-organization deadline because a cold client fetch is slower than a
 * session call, short enough that a user on a blocked network is offered a control rather than a
 * placeholder.
 */
export const CLERK_LOAD_DEADLINE_MS = 20000;

let expired = false;
let timer: ReturnType<typeof setTimeout> | undefined;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/**
 * Starts the bound on the authentication client's load.
 *
 * The vendor exposes an error status for a load that fails, but none for one that hangs — a
 * request that neither resolves nor rejects leaves every `isLoaded` false indefinitely, with no
 * event to react to. This supplies the missing terminal transition. Idempotent: repeated starts
 * keep the first deadline rather than pushing it forward, so a re-rendering caller cannot postpone
 * the bound indefinitely.
 */
export function startClerkLoadDeadline(): void {
  if (timer !== undefined || expired) return;
  timer = setTimeout(() => {
    expired = true;
    emit();
  }, CLERK_LOAD_DEADLINE_MS);
}

/** Cancels the bound once the client loads, so a later render cannot report a stale expiry. */
export function clearClerkLoadDeadline(): void {
  clearTimeout(timer);
  timer = undefined;
  expired = false;
  emit();
}

export function getClerkLoadExpired(): boolean {
  return expired;
}

export function subscribeToClerkLoadDeadline(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam: clears the value and any pending timer, leaving subscriptions intact. */
export function resetClerkLoadDeadline(): void {
  clearTimeout(timer);
  timer = undefined;
  expired = false;
}
